// Pure record-kind → route/behavior map for the cleanup drawer (Joy
// 2026-07-01). The drawer used to treat EVERY non-household ref as a
// contact, so clicking a policy (kind "service_record") on an issue
// like "When does this policy expire?" fetched
// GET /api/contacts/{service_record_id} → guaranteed 404 → a false
// "That record no longer exists (merged or archived)" — Joy skipped
// real work because the drawer said the record was gone. Kinds now
// route to their real detail endpoint/page.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/
// without dragging the DOM-typed web/src/api.ts into the root tsconfig
// program — same convention as household-cluster-predicate.ts.

// How the drawer loads a preview for a record kind:
//   contact  → GET /api/contacts/:id
//   household → GET /api/households/:id
//   service_record → GET /api/service-records/:id
//   transaction → GET /api/transactions/:id  (Processing records)
//   none     → no preview endpoint; the drawer shows the deep link only
export type DrawerFetchKind =
  | "contact"
  | "household"
  | "service_record"
  | "transaction"
  | "none";

export function drawerFetchKind(kind: string): DrawerFetchKind {
  switch (kind) {
    case "household":
      return "household";
    case "contact":
      return "contact";
    // A cleanup "vendor" ref is a row in the vendors list (a lender or a
    // carrier — the mortgage lender-duplicate and carrier-consolidation
    // detectors), NOT a vendor-type contact, so fetching it as a contact
    // 404'd into a false "That record no longer exists" (2026-10-02).
    // No inline preview; the deep link opens the vendor's own page.
    case "vendor":
      return "none";
    case "service_record":
      return "service_record";
    case "transaction":
      // Processing records — realty deals, loan progressions, policy
      // issuance. The transaction detail endpoint carries the property
      // address, buy/sell side, close date, sale price, household +
      // advisor, so a steward can identify the deal at a glance without
      // leaving Cleanup for ActiveCampaign (Nate 2026-07-07: opening a
      // realty cleanup issue gave "no idea which Solis this is").
      return "transaction";
    default:
      // opportunity / activity / policy — no drawer preview; the deep
      // link (when one exists) is the path, never a wrong-endpoint fetch
      // that 404s into a false "record no longer exists".
      return "none";
  }
}

// The full-record page for a record kind, or null when none exists
// (e.g. "activity" refs like license rows — no standalone page).
export function recordDeepLink(kind: string, id: string): string | null {
  const enc = encodeURIComponent(id);
  switch (kind) {
    case "household":
      return `/households/${enc}`;
    case "contact":
      return `/contacts/${enc}`;
    // A vendors-list row has its own page (not a contact).
    case "vendor":
      return `/vendors/${enc}`;
    // An employer (employer groups) has its own page, not a household's.
    case "business_entity":
      return `/employers/${enc}`;
    case "service_record":
      return `/service-records/${enc}`;
    case "opportunity":
      return `/opportunities/${enc}`;
    case "transaction":
      return `/processing/${enc}`;
    case "rhythm_instance":
      // Ongoing Care plan detail — where the "Turn on" affordance lives,
      // so the care-plan-waiting-to-start issue is never a dead end.
      return `/ongoing-care/${enc}`;
    default:
      return null;
  }
}

// The drawer's archive affordance goes through the cleanup "archive"
// verb. From the drawer it is offered on contacts and households only:
// a policy/opportunity ref has its own detector-driven archive, and a
// vendor (a row in the vendors list) has no archive anywhere in the OS —
// the route refuses it with archive_unsupported_kind (2026-10-02).
export function canArchiveFromDrawer(kind: string): boolean {
  return kind === "contact" || kind === "household";
}

// Whether a record opened in the drawer is one of the issue's OWN
// records. The decide route archives only a record the issue lists (the
// same pin /undo and rollback apply), so the drawer offers Archive only
// then. A record reached from research instead (a duplicate contact's
// household, opened from the roster) gets a pointer to its own page.
export function isRecordOnIssue(
  record: { kind: string; id: string },
  issueRecords: ReadonlyArray<{ kind: string; id: string }>,
): boolean {
  return issueRecords.some((r) => r.kind === record.kind && r.id === record.id);
}
